import React from 'react';
import {
  DataGrid,
  GridActionsCellItem
} from '@mui/x-data-grid';
import {
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Button,
  Autocomplete,
  TextField,
  Paper,
  Divider
} from '@mui/material';
import { getRequest } from 'Includes/api/apicall';
import { GET_URL } from 'Includes/urls';
import CompanyBillingSummaryModal from 'Components/CompanyBillingSummaryModal';
import CoordinatorWiseSummaryModal from 'Components/CoordinatorWiseSummaryModal';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { loadBillingFilters, saveBillingFilters } from 'Includes/billingFilterStorage';
import OrdersSourceToggle, { loadOrdersSource } from 'Components/OrdersSourceToggle';
import { TEAM_STATUS_CHOICES } from '../../Includes/teamStatus';
import { useNavigate } from 'react-router-dom';

function CompanyFinanceSummaryList(props) {
  const storedFilters = loadBillingFilters();
  const navigate = useNavigate();

  const [academicYearList, setAcademicYearList] = React.useState([])
  const [selectedAcademicYear, setSelectedAcademicYear] = React.useState(storedFilters.academicYearId || '')
  const [statusByTeamFilter, setStatusByTeamFilter] = React.useState('all')
  const [clientBillingStatusFilter, setClientBillingStatusFilter] = React.useState('')
  const [coordinatorFilter, setCoordinatorFilter] = React.useState('')
  const [companyOptions, setCompanyOptions] = React.useState([])
  const [selectedCompanies, setSelectedCompanies] = React.useState([])
  const [serviceList, setServiceList] = React.useState([])
  const [selectedService, setSelectedService] = React.useState(null)
  const [paidCompare, setPaidCompare] = React.useState('')
  const [paidPercent, setPaidPercent] = React.useState('')
  const [users, setUsers] = React.useState([])
  const [rows, setRows] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [rowData, setrowData] = React.useState(false)

  const [sortModel, setSortModel] = React.useState([
    { field: 'balance_to_be_paid_by_today', sort: 'desc' }
  ])

  const [page, setPage] = React.useState(0)
  const [pageSize, setPageSize] = React.useState(10)
  const [rowCount, setRowCount] = React.useState(0)
  const [overallSummaryData, setOverallSummaryData] = React.useState(null)

  const [detailOpen, setDetailOpen] = React.useState(false)
  const [detailData, setDetailData] = React.useState(null)
  const [coordinatorSummaryOpen, setCoordinatorSummaryOpen] = React.useState(false)
  const [coordinatorSummaryData, setCoordinatorSummaryData] = React.useState([])
  const [coordinatorSummaryLoading, setCoordinatorSummaryLoading] = React.useState(false)
  const [orderSource, setOrderSource] = React.useState(loadOrdersSource())

  const formatAmount = (v) => {
    const n = parseFloat(v || 0);
    return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  };

  const overallSummary = React.useMemo(() => {
    if (!overallSummaryData) {
      return {
        totals: { order: 0, issued: 0, invoiced: 0, paid: 0, balance: 0, discount: 0, dueByToday: 0, balanceToPayByToday: 0 },
        counts: {}
      }
    }
    return overallSummaryData
  }, [overallSummaryData])

  React.useEffect(() => {
    getAcademicYearList()
    getUsers()
    getServiceList()
    getCompanyOptions()
  }, [])

  const getAcademicYearList = () => {
    getRequest(GET_URL.academicyear.api, {}, props).then((response) => {
      if (response?.status === 200) {
        const list = response.data.data || [];
        setAcademicYearList(list);

        if (selectedAcademicYear) {
          fetchSummary(selectedAcademicYear);
        }
      }
    });
  };

  const getServiceList = () => {
    getRequest(GET_URL.services?.api, {}, props).then((response) => {
      if (response?.status === 200) {
        setServiceList(response.data.data || []);
      }
    });
  };

  const getCompanyOptions = () => {
    getRequest(GET_URL.company.api, { limit: 10000, pageno: 1 }, props).then((response) => {
      if (response?.status === 200) {
        const raw = response.data.data || response.data || []
        setCompanyOptions(Array.isArray(raw) ? raw : [])
      }
    })
  }

  const paidPercentParams = () => {
    const percent = String(paidPercent ?? '').trim()
    if (!paidCompare || percent === '') return {}
    return { paid_compare: paidCompare, paid_percent: percent }
  }

  const companyParams = () => {
    if (!selectedCompanies.length) return {}
    return { company: selectedCompanies.map((c) => c.id).join(',') }
  }

  const getUsers = () => {
    getRequest(GET_URL.user.api, { groups: '4' }, props).then((response) => {
      if (response?.status === 200) {
        setUsers(response.data.data || []);
      }
    })
  }

  const fetchSummary = (yearId) => {
    if (!yearId) return

    setLoading(true)

    const params = {
      year: yearId,
      limit: pageSize,
      pageno: page + 1,
      ...(statusByTeamFilter && { status_by_team: statusByTeamFilter }),
      ...(clientBillingStatusFilter && { client_billing_status: clientBillingStatusFilter }),
      ...(coordinatorFilter && { coordinator: coordinatorFilter }),
      ...companyParams(),
      ...(selectedService?.id && { service: selectedService.id }),
      ...paidPercentParams(),
      order_source: orderSource,
      ...(sortModel?.length && {
        ordering: sortModel[0].sort === 'desc'
          ? `-${sortModel[0].field}`
          : sortModel[0].field
      })
    }

    getRequest(GET_URL.billing_config.api, { ...params, finance_summary: 1 }, props)
      .then((response) => {
        setLoading(false)
        if (response?.status === 200) {
          setRows(response.data?.data || [])
          setRowCount(response.data?.count || 0)
          setOverallSummaryData(response.data?.summary || null)
        }
      })
      .catch(() => setLoading(false))
  }

  React.useEffect(() => {
    if (selectedAcademicYear) {
      fetchSummary(selectedAcademicYear)
    }
  }, [
    selectedAcademicYear,
    statusByTeamFilter,
    clientBillingStatusFilter,
    coordinatorFilter,
    selectedCompanies,
    selectedService,
    paidCompare,
    paidPercent,
    sortModel,
    page,
    pageSize,
    orderSource,
  ])

  const handlePageChange = (newPage) => {
    setPage(newPage)
  }

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize)
    setPage(0)
  }

  const columns = [
    { field: 'company_name',
      headerName: 'Company',
      width: 220,
      renderCell: (params) => {
        const balance = parseFloat(params.row.balance_amount || 0)
        const due = parseFloat(params.row.balance_to_be_paid_by_today || 0)

        let color = 'inherit'
        if (due > 0) color = '#d32f2f'
        else if (balance <= 0) color = '#2e7d32'

        return (
          <Typography fontWeight={600} color={color}>
            {params.value || '-'}
          </Typography>
        )
      }
    },
    { field: 'status_by_team', headerName: 'Client Status (Team)', width: 170 },
    { field: 'order_total_amount', headerName: 'Order Total', width: 140 },
    { field: 'issued_invoice_total_amount', headerName: 'Issued Invoice', width: 160 },
    { field: 'paid_total_amount', headerName: 'Paid', width: 130 },
    {
      field: 'paid_percentage',
      headerName: 'Paid %',
      width: 110,
      renderCell: (params) => `${parseFloat(params.value || 0).toFixed(2)}%`
    },
    { field: 'balance_amount', headerName: 'Pending', width: 130 },
    { field: 'coordinator_name', headerName: 'Coordinator', width: 180 },
    {
      field: 'actions',
      type: 'actions',
      getActions: (params) => [
        <GridActionsCellItem label="View Details" onClick={() => openDetails(params.row)} showInMenu />
      ]
    }
  ]

  const openDetails = (row) => {
    if (orderSource === 'orders_v2' || row?.source === 'orders_v2') {
      const orderId = row.order_v2_id || row.config_id || row.id;
      if (orderId) {
        navigate(`/billing/orders-v2/form?id=${orderId}`);
      }
      return;
    }
    setDetailOpen(true)
    setDetailData(null)

    getRequest(`${GET_URL.billing_config.api}${row.config_id}/`, {}, props)
      .then(res => setDetailData(res?.data || null))
  }

  const handleDownloadExcel = () => {
    if (!selectedAcademicYear) return
    getRequest(
      GET_URL.billing_config.api,
      {
        year: selectedAcademicYear,
        ...(statusByTeamFilter && { status_by_team: statusByTeamFilter }),
        ...(clientBillingStatusFilter && { client_billing_status: clientBillingStatusFilter }),
        ...(coordinatorFilter && { coordinator: coordinatorFilter }),
        ...companyParams(),
        ...(selectedService?.id && { service: selectedService.id }),
        ...paidPercentParams(),
        finance_summary_export: 1,
        order_source: orderSource,
      },
      { ...props, responseType: 'blob' }
    )
      .then((response) => {
        if (response && response.status === 200) {
          const contentType =
            response?.headers?.['content-type'] ||
            response?.headers?.['Content-Type'] ||
            'application/octet-stream'
          const responseBlob = response?.data instanceof Blob
            ? response.data
            : new Blob([response.data], { type: contentType })
          const fileURL = window.URL.createObjectURL(responseBlob)
          const contentDisposition =
            response?.headers?.['content-disposition'] || response?.headers?.['Content-Disposition']
          let filename = `companies_billing_summary_year_${selectedAcademicYear}.xlsx`
          if (typeof contentDisposition === 'string') {
            const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)
            const asciiMatch = contentDisposition.match(/filename="?([^"]+)"?/i)
            const extracted = utf8Match?.[1] ? decodeURIComponent(utf8Match[1]) : asciiMatch?.[1]
            if (extracted) filename = extracted
          }
          const link = document.createElement('a')
          link.href = fileURL
          link.download = filename
          document.body.appendChild(link)
          link.click()
          window.URL.revokeObjectURL(fileURL)
          document.body.removeChild(link)
        }
      })
  }

  const orderTotal = parseFloat(overallSummary.totals.order || 0)
  const paidTotal = parseFloat(overallSummary.totals.paid || 0)
  const overallPaidPercent = orderTotal ? `${((paidTotal / orderTotal) * 100).toFixed(2)}%` : '0.00%'

  return (
    <Box p={2}>

      {/* HEADER */}
        <Grid container alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h6" fontWeight={700}>
              Companies Billing Summary
            </Typography>
          </Grid>

          <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, alignItems: 'center', gap: 2, mt: { xs: 1, md: 0 } }}>
            <OrdersSourceToggle
              value={orderSource}
              onChange={(next) => {
                setOrderSource(next);
                setPage(0);
              }}
            />
            <Button
              variant="contained"
              startIcon={<DownloadOutlinedIcon />}
              onClick={handleDownloadExcel}
              disabled={!selectedAcademicYear}
            >
              Download Excel
            </Button>
          </Grid>
        </Grid>

      {/* FILTERS */}
      <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <Grid container spacing={2}>
          <Grid item md={3} xs={12}>
            <FormControl fullWidth>
              <InputLabel>Academic Year</InputLabel>
              <Select
                value={selectedAcademicYear}
                onChange={(e) => {
                  setSelectedAcademicYear(e.target.value)
                  saveBillingFilters({ academicYearId: e.target.value })
                  setPage(0)
                }}
                label="Academic Year"
              >
                {academicYearList.map((y) => (
                  <MenuItem key={y.id} value={y.id}>{y.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item md={3} xs={12}>
            <FormControl fullWidth>
              <InputLabel>Client Status (Team)</InputLabel>
              <Select
                value={statusByTeamFilter}
                label="Client Status (Team)"
                onChange={(e) => {
                  setStatusByTeamFilter(e.target.value)
                  setPage(0)
                }}
              >
                {TEAM_STATUS_CHOICES.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item md={3} xs={12}>
            <FormControl fullWidth>
              <InputLabel>Billing Status</InputLabel>
              <Select
                value={clientBillingStatusFilter}
                onChange={(e) => {
                  setClientBillingStatusFilter(e.target.value)
                  setPage(0)
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="GOOD">GOOD</MenuItem>
                <MenuItem value="CRITICAL">CRITICAL</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item md={3} xs={12}>
            <Autocomplete
              options={serviceList}
              getOptionLabel={(o) => o.name || ''}
              value={selectedService}
              onChange={(e, v) => {
                setSelectedService(v)
                setPage(0)
              }}
              renderInput={(params) => <TextField {...params} label="Service" />}
            />
          </Grid>

          <Grid item md={6} xs={12}>
            <Autocomplete
              multiple
              options={companyOptions}
              getOptionLabel={(o) => o.name || o.company_name || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={selectedCompanies}
              onChange={(e, v) => {
                setSelectedCompanies(v)
                setPage(0)
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Companies"
                  placeholder={selectedCompanies.length ? '' : 'All companies'}
                />
              )}
            />
          </Grid>

          <Grid item md={3} xs={12}>
            <FormControl fullWidth>
              <InputLabel>Paid vs total</InputLabel>
              <Select
                value={paidCompare}
                label="Paid vs total"
                onChange={(e) => {
                  setPaidCompare(e.target.value)
                  setPage(0)
                }}
              >
                <MenuItem value="">Any percentage</MenuItem>
                <MenuItem value="gt">Paid more than</MenuItem>
                <MenuItem value="lt">Paid less than</MenuItem>
                <MenuItem value="eq">Paid equal to</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item md={3} xs={12}>
            <TextField
              fullWidth
              type="number"
              label="% of total amount"
              value={paidPercent}
              disabled={!paidCompare}
              inputProps={{ min: 0, max: 100, step: '0.01' }}
              onChange={(e) => {
                setPaidPercent(e.target.value)
                setPage(0)
              }}
              placeholder="e.g. 50"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* SUMMARY */}
      {selectedAcademicYear && (
        <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 3 }}>
          <Typography fontWeight={600} mb={1}>
            {selectedCompanies.length ? 'Selected companies' : 'All companies'}
            {selectedService?.name ? ` with ${selectedService.name}` : ''}
            {' — paid and pending'}
          </Typography>
          <Divider sx={{ mb: 1 }} />

          <Grid container spacing={2}>
            <Grid item md={2}>Companies: {rowCount}</Grid>
            <Grid item md={2}>Order: {formatAmount(overallSummary.totals.order)}</Grid>
            <Grid item md={2}>Paid: {formatAmount(overallSummary.totals.paid)}</Grid>
            <Grid item md={2}>Pending: {formatAmount(overallSummary.totals.balance)}</Grid>
            <Grid item md={2}>Paid %: {overallPaidPercent}</Grid>
            <Grid item md={2}>Due Today: {formatAmount(overallSummary.totals.balanceToPayByToday)}</Grid>
          </Grid>
        </Paper>
      )}

      {/* TABLE */}
      <Paper elevation={2} sx={{ borderRadius: 3 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.order_v2_id || row.config_id || row.id}
          loading={loading}
          autoHeight
          pagination
          paginationMode="server"
          page={page}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          rowsPerPageOptions={[10, 20, 50]}
          rowCount={rowCount}
          disableSelectionOnClick
          sx={{
            border: 0,
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f5f7fb',
              fontWeight: 'bold'
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#f9fbff'
            }
          }}
        />
      </Paper>

      <CompanyBillingSummaryModal
        open={detailOpen}
        loading={detailOpen && !detailData}
        data={detailData}
        onClose={() => setDetailOpen(false)}
      />

      <CoordinatorWiseSummaryModal
        open={coordinatorSummaryOpen}
        loading={coordinatorSummaryLoading}
        data={coordinatorSummaryData}
        onClose={() => setCoordinatorSummaryOpen(false)}
      />
    </Box>
  )
}

export default CompanyFinanceSummaryList;